import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Alert, Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import api from '../api/api';

interface Props {
  open: boolean;
  onClose: () => void;
  student: { 
    id: number; 
    name: string; 
    notes?: string; 
    profile_picture?: string;
    parent_email?: string;
    parent_phone?: string;
    class_level: string;
    student_id?: string;
    date_of_birth?: string;
    gender?: string;
    parent_contact?: string;
    emergency_contact?: string;
    medical_info?: string;
    enrollment_date?: string;
    graduation_date?: string;
    status?: string;
  };
  onSaved: () => void;
}

const EditStudentDialog: React.FC<Props> = ({ open, onClose, student, onSaved }) => {
  const [name, setName] = useState(student.name);
  const [notes, setNotes] = useState(student.notes || '');
  const [error, setError] = useState('');
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [studentId, setStudentId] = useState(student.student_id || '');
  const [dateOfBirth, setDateOfBirth] = useState(student.date_of_birth || '');
  const [gender, setGender] = useState(student.gender || '');
  const [parentEmail, setParentEmail] = useState(student.parent_email || '');
  const [parentPhone, setParentPhone] = useState(student.parent_phone || '');
  const [parentContact, setParentContact] = useState(student.parent_contact || '');
  const [emergencyContact, setEmergencyContact] = useState(student.emergency_contact || '');
  const [medicalInfo, setMedicalInfo] = useState(student.medical_info || '');
  const [enrollmentDate, setEnrollmentDate] = useState(student.enrollment_date || '');
  const [graduationDate, setGraduationDate] = useState(student.graduation_date || '');
  const [status, setStatus] = useState(student.status || 'active');
  const [classLevel, setClassLevel] = useState(student.class_level || 'Grade 1');

  const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const classLevels = [
    { value: 'Pre-Primary 1', label: 'Pre-Primary 1' },
    { value: 'Pre-Primary 2', label: 'Pre-Primary 2' },
    { value: 'Grade 1', label: 'Grade 1' },
    { value: 'Grade 2', label: 'Grade 2' },
    { value: 'Grade 3', label: 'Grade 3' },
    { value: 'Grade 4', label: 'Grade 4' },
    { value: 'Grade 5', label: 'Grade 5' },
    { value: 'Grade 6', label: 'Grade 6' },
    { value: 'Grade 7', label: 'Grade 7' },
    { value: 'Grade 8', label: 'Grade 8' },
    { value: 'Grade 9', label: 'Grade 9' },
    { value: 'Grade 10', label: 'Grade 10' },
    { value: 'Grade 11', label: 'Grade 11' },
    { value: 'Grade 12', label: 'Grade 12' },
  ];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'graduated', label: 'Graduated' },
    { value: 'transferred', label: 'Transferred' },
  ];

  React.useEffect(() => {
    setName(student.name);
    setNotes(student.notes || '');
    setError('');
    setProfilePicture(null);
    setProfilePreview(null);
    setStudentId(student.student_id || '');
    setDateOfBirth(student.date_of_birth || '');
    setGender(student.gender || '');
    setParentEmail(student.parent_email || '');
    setParentPhone(student.parent_phone || '');
    setParentContact(student.parent_contact || '');
    setEmergencyContact(student.emergency_contact || '');
    setMedicalInfo(student.medical_info || '');
    setEnrollmentDate(student.enrollment_date || '');
    setGraduationDate(student.graduation_date || '');
    setStatus(student.status || 'active');
    setClassLevel(student.class_level || 'Grade 1');
  }, [student, open]);

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setProfilePicture(file);
    if (file) {
      setProfilePreview(URL.createObjectURL(file));
    } else {
      setProfilePreview(null);
    }
  };

  const handleSave = async () => {
    setError('');
    try {
      await api.put(`/students/${student.id}`, { 
        name, 
        notes: notes || null,
        student_id: studentId || null,
        date_of_birth: dateOfBirth || null,
        gender: gender || null,
        parent_email: parentEmail || null,
        parent_phone: parentPhone || null,
        parent_contact: parentContact || null,
        emergency_contact: emergencyContact || null,
        medical_info: medicalInfo || null,
        enrollment_date: enrollmentDate || null,
        graduation_date: graduationDate || null,
        status,
        class_level: classLevel
      });
      // Upload profile picture if selected
      if (profilePicture) {
        const picData = new FormData();
        picData.append('file', profilePicture);
        await api.post(`/students/${student.id}/profile_picture`, picData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      onSaved();
      onClose();
    } catch {
      setError('Failed to update student.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Student</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Basic Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField 
                label="Name" 
                value={name} 
                onChange={e => setName(e.target.value)} 
                required 
                fullWidth
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField 
                label="Student ID" 
                value={studentId} 
                onChange={e => setStudentId(e.target.value)} 
                fullWidth
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <FormControl fullWidth required>
                <InputLabel>Class Level</InputLabel>
                <Select
                  value={classLevel}
                  label="Class Level"
                  onChange={e => setClassLevel(e.target.value)}
                >
                  {classLevels.map((level) => (
                    <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={e => setStatus(e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField 
                label="Date of Birth" 
                type="date"
                value={dateOfBirth} 
                onChange={e => setDateOfBirth(e.target.value)} 
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={gender}
                  label="Gender"
                  onChange={e => setGender(e.target.value)}
                >
                  {genderOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Parent Information */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Parent Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField 
                label="Parent Email" 
                type="email"
                value={parentEmail} 
                onChange={e => setParentEmail(e.target.value)} 
                fullWidth
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField 
                label="Parent Phone" 
                value={parentPhone} 
                onChange={e => setParentPhone(e.target.value)} 
                fullWidth
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField 
                label="Parent Contact" 
                value={parentContact} 
                onChange={e => setParentContact(e.target.value)} 
                fullWidth
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField 
                label="Emergency Contact" 
                value={emergencyContact} 
                onChange={e => setEmergencyContact(e.target.value)} 
                fullWidth
              />
            </Box>
          </Box>

          {/* Academic Information */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Academic Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField 
                label="Enrollment Date" 
                type="date"
                value={enrollmentDate} 
                onChange={e => setEnrollmentDate(e.target.value)} 
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField 
                label="Graduation Date" 
                type="date"
                value={graduationDate} 
                onChange={e => setGraduationDate(e.target.value)} 
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          {/* Additional Information */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Additional Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField 
                label="Notes" 
                value={notes} 
                onChange={e => setNotes(e.target.value)} 
                multiline 
                rows={3}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField 
                label="Medical Information" 
                value={medicalInfo} 
                onChange={e => setMedicalInfo(e.target.value)} 
                multiline 
                rows={3}
                fullWidth
              />
            </Box>
          </Box>

          {/* Profile Picture */}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
            Profile Picture
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <Button variant="outlined" component="label" fullWidth>
                Upload New Profile Picture
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                  style={{ display: 'none' }}
                />
              </Button>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px', textAlign: 'center' }}>
              {(profilePreview || student.profile_picture) && (
                <img 
                  src={profilePreview || `${backendUrl}/static/profile_pictures/${student.profile_picture}`} 
                  alt="Profile" 
                  style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover', borderRadius: '8px' }} 
                />
              )}
            </Box>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save Changes</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditStudentDialog; 